import { useEffect, useMemo, useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import Sidebar from '../components/layout/sidebar/sidebar'
import { MainView } from '../components/layout/main'
import api from '../helpers/apiRequester'
import { getLoginUrl } from '../helpers/authConfig'
import { StatsView } from '../components/views'
import ReportDownload from '../components/reportDownload'
import styles from './pcp-referral-engagement-stats.module.scss'
import { Page, PageHeader } from '../ui'

const PAGE_SIZE = 25

function PcpReferralEngagementStats() {
    const [dataReady, setDataReady] = useState(false)
    const [loading, setLoading] = useState(false)
    const [overviewData, setOverviewData] = useState<any>()
    const [teamFilter, setTeamFilter] = useState<string>('all')
    const [activeView, setActiveView] = useState<'agents' | 'teams'>('agents')

    const teams: string[] = useMemo(() => {
        if (!overviewData?.ats) return []
        const names = overviewData.ats
            .map((a: any) => a.team_name)
            .filter((n: any): n is string => Boolean(n))
        return Array.from(new Set<string>(names)).sort()
    }, [overviewData])

    const filteredAts = useMemo(() => {
        if (!overviewData?.ats) return []
        if (teamFilter === 'all') return overviewData.ats
        if (teamFilter === 'unassigned') return overviewData.ats.filter((a: any) => !a.team_name)
        return overviewData.ats.filter((a: any) => a.team_name === teamFilter)
    }, [overviewData, teamFilter])

    const teamStats = useMemo(() => {
        if (!overviewData?.ats) return []
        const map: Record<string, { team: string; agents: number; sentToday: number; sentTotal: number; signedToday: number; signedTotal: number }> = {}
        for (const a of overviewData.ats) {
            const key = a.team_name || 'Unassigned'
            if (!map[key]) map[key] = { team: key, agents: 0, sentToday: 0, sentTotal: 0, signedToday: 0, signedTotal: 0 }
            map[key].agents++
            map[key].sentToday += a.sentToday || 0
            map[key].sentTotal += a.sentTotal || 0
            map[key].signedToday += a.signedToday || 0
            map[key].signedTotal += a.signedTotal || 0
        }
        return Object.values(map).sort((a, b) => {
            if (a.team === 'Unassigned') return 1
            if (b.team === 'Unassigned') return -1
            return a.team.localeCompare(b.team)
        })
    }, [overviewData])

    // Build the filter options that get passed to <ReportDownload>. `agents` is a
    // flat list (agent label + the identifier that lives in the `agent` column on
    // sent/signed rows); `teamsAgents` maps each team -> its members' identifiers
    // so picking a team filters to that team's combined activity.
    const reportFilters = useMemo(() => {
        const ats: any[] = overviewData?.ats || []
        const agents = ats.map((a) => ({ label: a.agent, value: a.agent }))
        const teamsAgents: Record<string, string[]> = {}
        const agentToTeam: Record<string, string> = {}
        for (const a of ats) {
            agentToTeam[a.agent.toLowerCase()] = a.team_name || 'Unassigned'
            if (!a.team_name) continue
            if (!teamsAgents[a.team_name]) teamsAgents[a.team_name] = []
            teamsAgents[a.team_name].push(a.agent)
        }
        return { agents, teamsAgents, agentToTeam }
    }, [overviewData])

    useEffect(() => {
        if (!dataReady && !loading) {
            setLoading(true)
            getData()
        }
    }, [dataReady, loading])

    const getData = async () => {
        Loading.circle('Loading...')
        try {
            const [statsResponse, usersResponse] = await Promise.all([
                api({ url: '/pcp-referral-engagement-stats' }),
                api({ url: '/pcp-referral-users-list' }).catch(() => null)
            ])
            const data = statsResponse.data

            const teamByEmail: Record<string, string | null> = {}
            if (usersResponse?.data?.users) {
                for (const u of usersResponse.data.users) {
                    teamByEmail[u.email?.toLowerCase()] = u.team_name ?? null
                }
            }

            if (data.ats) {
                data.ats = data.ats.map((a: any) => ({
                    ...a,
                    team_name: teamByEmail[a.email?.toLowerCase()] ?? null
                }))
            }

            setOverviewData(data)
            setDataReady(true)
            Loading.remove()
        } catch (error: any) {
            console.log(error)
            Loading.remove()
            if (error?.response?.status == 401) {
                Report.failure('Session Timed Out', 'Please log in again to continue.', 'Okay', () => {
                    window.location.href = getLoginUrl()
                })
            } else {
                window.location.href = getLoginUrl()
            }
        }
    }

    return (
        <div className="home">
            <Sidebar />
            <MainView>
                <Page>
                    <PageHeader eyebrow="PCP Referral" title={'PCP Referral Engagement Stats'} />
                    <div>
                        {overviewData?.figures && <StatsView stats={overviewData.figures} />}
                        {overviewData?.ats && (
                            <>
                                <ReportDownload
                                    reportType="pcp-referral-signed"
                                    reportTitle="Signatures Report"
                                    filters={{ ...reportFilters, filterDataset: 'signed' }}
                                />
                                <ReportDownload
                                    reportType="pcp-referral-sent"
                                    reportTitle="Links Sent Report"
                                    filters={{ ...reportFilters, filterDataset: 'sent' }}
                                />
                            </>
                        )}
                        {overviewData?.ats && (
                            <div className={styles.statsSection}>
                                {/* Underline tab bar */}
                                <div className={styles.tabRow}>
                                    <div className={styles.tabs}>
                                    {(['agents', 'teams'] as const).map((v) => (
                                        <button
                                            key={v}
                                            type="button"
                                            onClick={() => setActiveView(v)}
                                            className={`${styles.tab} ${activeView === v ? styles.tabActive : ''}`}
                                        >
                                            {v === 'agents' ? 'Agent Stats' : 'Team Stats'}
                                        </button>
                                    ))}
                                    </div>
                                    <button
                                        onClick={() => activeView === 'agents'
                                            ? downloadCsv(filteredAts.map((r: any) => ({
                                                Agent: r.agent,
                                                Team: r.team_name || 'Unassigned',
                                                'Sent Today': r.sentToday,
                                                'Sent Total': r.sentTotal,
                                                'Signed Today': r.signedToday,
                                                'Signed Total': r.signedTotal,
                                            })), `agent-stats-${teamFilter}`)
                                            : downloadCsv(teamStats.map((r: any) => ({
                                                Team: r.team,
                                                Agents: r.agents,
                                                'Sent Today': r.sentToday,
                                                'Sent Total': r.sentTotal,
                                                'Signed Today': r.signedToday,
                                                'Signed Total': r.signedTotal,
                                            })), 'team-stats')
                                        }
                                        className={styles.exportBtn}
                                    >
                                        ↓ Download CSV
                                    </button>
                                </div>

                                {activeView === 'agents' && (
                                    <>
                                        {teams.length > 0 && (
                                            <div className={styles.chipRow}>
                                                {(['all', ...teams, 'unassigned'] as string[]).map((t) => (
                                                    <button
                                                        key={t}
                                                        type="button"
                                                        onClick={() => setTeamFilter(t)}
                                                        className={`${styles.chip} ${teamFilter === t ? styles.chipActive : ''}`}
                                                    >
                                                        {t === 'all' ? 'All Teams' : t === 'unassigned' ? 'Unassigned' : t}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        <AgentStatsTable rows={filteredAts} />
                                    </>
                                )}

                                {activeView === 'teams' && (
                                    <TeamStatsTable rows={teamStats} />
                                )}
                            </div>
                        )}
                    </div>
                </Page>
            </MainView>
        </div>
    )
}

export default PcpReferralEngagementStats

// ─── CSV export helper ───────────────────────────────────────────────────────

const downloadCsv = (rows: Record<string, any>[], filename: string) => {
    if (!rows.length) return
    const headers = Object.keys(rows[0])
    const escape = (v: any) => {
        const s = String(v ?? '')
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s
    }
    const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

// ─── Agent Stats table (paginated) ──────────────────────────────────────────

const AgentStatsTable = ({ rows }: { rows: any[] }) => {
    const [page, setPage] = useState(1)
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
    const safePage = Math.min(page, totalPages)
    const start = (safePage - 1) * PAGE_SIZE
    const pageRows = rows.slice(start, start + PAGE_SIZE)

    if (!rows.length) return <p className={styles.emptyText}>No agent data available.</p>

    return (
        <div className={styles.tableWrapper}>
            <table className={styles.statsTable}>
                <thead>
                    <tr>
                        <th>Agent</th>
                        <th>Team</th>
                        <th className={styles.center}>Sent Today</th>
                        <th className={styles.center}>Sent Total</th>
                        <th className={styles.center}>Signed Today</th>
                        <th className={styles.center}>Signed Total</th>
                    </tr>
                </thead>
                <tbody>
                    {pageRows.map((r: any, i: number) => (
                        <tr key={i}>
                            <td>{r.agent}</td>
                            <td>{r.team_name || 'Unassigned'}</td>
                            <td className={styles.center}>{r.sentToday}</td>
                            <td className={styles.center}>{r.sentTotal}</td>
                            <td className={styles.center}>{r.signedToday}</td>
                            <td className={styles.center}>{r.signedTotal}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {totalPages > 1 && (
                <div className={styles.paginationSection}>
                    <span className={styles.paginationInfo}>
                        Showing {start + 1}–{Math.min(start + PAGE_SIZE, rows.length)} of {rows.length} agents
                    </span>
                    <div className={styles.paginationControls}>
                        <button
                            className={styles.paginationButton}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={safePage === 1}
                        >
                            ‹
                        </button>
                        <Pagination current={safePage} total={totalPages} onChange={setPage} />
                        <button
                            className={styles.paginationButton}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={safePage === totalPages}
                        >
                            ›
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

// ─── Team Stats table ────────────────────────────────────────────────────────

interface TeamStatsRow {
    team: string
    agents: number
    sentToday: number
    sentTotal: number
    signedToday: number
    signedTotal: number
}

const TeamStatsTable = ({ rows }: { rows: TeamStatsRow[] }) => {
    if (!rows.length) return <p className={styles.emptyText}>No team data available.</p>
    return (
        <div className={styles.tableWrapper}>
            <table className={styles.statsTable}>
                <thead>
                    <tr>
                        <th>Team</th>
                        <th className={styles.center}>Agents</th>
                        <th className={styles.center}>Sent Today</th>
                        <th className={styles.center}>Sent Total</th>
                        <th className={styles.center}>Signed Today</th>
                        <th className={styles.center}>Signed Total</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.team}>
                            <td>{r.team}</td>
                            <td className={styles.center}>{r.agents}</td>
                            <td className={styles.center}>{r.sentToday}</td>
                            <td className={styles.center}>{r.sentTotal}</td>
                            <td className={styles.center}>{r.signedToday}</td>
                            <td className={styles.center}>{r.signedTotal}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

// ─── Pagination helper ───────────────────────────────────────────────────────

const Pagination = ({ current, total, onChange }: { current: number; total: number; onChange: (p: number) => void }) => {
    const pages: (number | '...')[] = []
    if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
    } else {
        pages.push(1)
        if (current > 3) pages.push('...')
        for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
        if (current < total - 2) pages.push('...')
        pages.push(total)
    }
    return (
        <>
            {pages.map((p, i) =>
                p === '...' ? (
                    <span key={`e-${i}`} className={styles.paginationEllipsis}>…</span>
                ) : (
                    <button
                        key={p}
                        onClick={() => onChange(p)}
                        className={`${styles.paginationButton} ${current === p ? styles.paginationButtonActive : ''}`}
                    >
                        {p}
                    </button>
                )
            )}
        </>
    )
}
